import _ from 'underscore'
import moment from 'moment'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'

import { Button, type ButtonStyles } from '@nexiahome/ui-common/components/primitives/buttons'

import Table from 'components/Table'
import TextInput from 'components/TextInput'

import Component from 'pages/Device/SystemComponentsPanel/Component'

import useDefaultFirmwareLoads from 'hooks/useDefaultFirmwareLoads'
import useSystemComponents from 'hooks/useSystemComponents'

import { SystemComponent } from 'types/SystemComponent'

import { screenSize } from 'styles/shared'
import { color } from 'styles/colors'

export type Props = {
  components: Required<Array<SystemComponent>>
  onUpdateCallBack: (value: number) => void
  deviceId: string
  readOnly: boolean
}

const nonCommunicatingComponent = {
  id: null,
  description: '',
  isConnected: null,
  batteryLevel: '',
  modelNumber: '',
  serialNumber: '',
  versionNumber: ''
}

const ComponentList = ({ components, onUpdateCallBack, deviceId, readOnly }: Props) => {
  const [refreshTimestamp, setRefreshTimestamp] = useState(moment().valueOf())
  const { systemComponents, areSystemComponentsLoaded } = useSystemComponents(deviceId, refreshTimestamp)
  const [componentList, setComponentList] = useState<Array<SystemComponent>>([])
  const [editableComponent, setEditableComponent] = useState<SystemComponent>(nonCommunicatingComponent)
  const [disabledButton, setDisabledButton] = useState(false)

  useDefaultFirmwareLoads() // retrieves loads into store context for child components

  useEffect(() => {
    if (systemComponents && areSystemComponentsLoaded) {
      setComponentList(systemComponents)
      setDisabledButton(false)
      onUpdateCallBack(components.length + systemComponents.length)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areSystemComponentsLoaded])

  const onAddComponent = () => {
    setEditableComponent(nonCommunicatingComponent)
    setComponentList([...componentList, { ...nonCommunicatingComponent }])
    setDisabledButton(true)
  }

  const onCancel = (updateComponent: SystemComponent) => {
    const modifiedList = _.without(componentList, updateComponent)
    setComponentList([...modifiedList])
    setDisabledButton(false)
  }

  const onRefresh = useCallback(() => {
    setRefreshTimestamp(moment().valueOf())
  }, [])

  const setEdit = (component?: SystemComponent) => {
    if (component) {
      setEditableComponent(component)
    } else {
      setEditableComponent(nonCommunicatingComponent)
    }
    setDisabledButton(!disabledButton)
  }

  return (
    <S.Container>
      {
        components.concat(componentList).map((component) => {
          return (
            <S.Row key={component.description + component.modelNumber + component.serialNumber} >
              <Component
                disabled={disabledButton}
                editableComponent={editableComponent}
                component={component}
                deviceId={deviceId}
                onCancelCallBack={onCancel}
                onRefreshCallBack={onRefresh}
                setEditCallBack={setEdit}
              />
            </S.Row>
          )
        })
      }
      {
        !readOnly && (
          <Button
            decoration="secondary"
            enabled={!disabledButton}
            text="Add Component"
            onPress={onAddComponent}
            styles={{ button: AddComponentButtonStyle.button }}
          />)
      }
    </S.Container>
  )
}

export default ComponentList

const AddComponentButtonStyle: ButtonStyles = {
  button: {
    alignSelf: 'flex-start',
    marginTop: 7,
    marginBottom: 15,
    marginLeft: 15
  }
}

const S = {
  Container: styled.div`
    display: flex;
    flex-direction: column;
    border: 1px solid ${color('columnBorder')};
  `,
  Row: styled(Table.Row)`
    border-bottom: none;
    padding: 3px 0;
    min-height: 43px;
    align-items: flex-start;
    display: grid;
    grid-template-columns: 18% 18% 15% 18% 15% auto;

    @media ${screenSize.small} , ${screenSize.medium} {
      display: flex;
      flex-direction: column;
      align-items: unset;
      padding: 10px 0;
    }
    `,
  Cell: styled(Table.Cell)`
    padding-left: 15px;
    `,
  Input: styled(TextInput)`
    margin-right: 10px;

    > input:first-of-type {
      font-size: 0.85rem;
      border-radius: 4px;
      line-height: 1;
      height: 26px;
      margin: 0;
      padding-top: 0;
      padding-bottom: 0;
    }`
}
